import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Tooltip, IconButton, Switch, Grid, TextField, MenuItem, DialogContentText,
    Chip, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';

const DIAS = [
    { key: 'SEGUNDA', label: 'Segunda', labelFull: 'Segunda-feira' },
    { key: 'TERCA',   label: 'Terça',   labelFull: 'Terça-feira' },
    { key: 'QUARTA',  label: 'Quarta',  labelFull: 'Quarta-feira' },
    { key: 'QUINTA',  label: 'Quinta',  labelFull: 'Quinta-feira' },
    { key: 'SEXTA',   label: 'Sexta',   labelFull: 'Sexta-feira' },
];

const initialForm = { diaSemana: '', horaInicio: '08:00', horaFim: '09:00', ativo: true };

function toMinutes(hhmm) {
    if (!hhmm) return 0;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
}

function toLocalDateString(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function MiniTimeline({ blocos, expInicio, expFim }) {
    const expStart = toMinutes(expInicio);
    const expEnd   = toMinutes(expFim);
    const total    = expEnd - expStart;
    if (total <= 0) return null;

    return (
        <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: '#eee', my: 1.5, overflow: 'hidden' }}>
            {blocos.map(b => {
                const left  = ((toMinutes(b.horaInicio) - expStart) / total) * 100;
                const width = ((toMinutes(b.horaFim) - toMinutes(b.horaInicio)) / total) * 100;
                return (
                    <Box key={b.id} sx={{
                        position: 'absolute',
                        left: `${Math.max(0, left)}%`,
                        width: `${Math.min(100 - Math.max(0, left), width)}%`,
                        height: '100%',
                        bgcolor: b.ativo ? '#1ABB9C' : '#bdbdbd',
                        borderRadius: 3,
                    }} />
                );
            })}
        </Box>
    );
}

function BlocoHorario({ bloco, onToggle, onDelete }) {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: bloco.ativo ? 'rgba(26,187,156,0.3)' : 'divider',
            bgcolor: bloco.ativo ? 'rgba(26,187,156,0.06)' : 'action.hover',
            transition: 'all 0.2s',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '0.9rem', color: bloco.ativo ? '#1ABB9C' : 'text.disabled' }} />
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.82rem',
                        color: bloco.ativo ? 'text.primary' : 'text.disabled',
                        textDecoration: bloco.ativo ? 'none' : 'line-through',
                    }}
                >
                    {bloco.horaInicio} – {bloco.horaFim}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Tooltip title={bloco.ativo ? 'Pausar' : 'Ativar'}>
                    <IconButton size="small" onClick={() => onToggle(bloco.id)} sx={{ p: 0.4 }}>
                        {bloco.ativo
                            ? <PauseCircleIcon sx={{ fontSize: '1rem', color: '#1ABB9C' }} />
                            : <PlayCircleIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => onDelete(bloco.id)} sx={{ p: 0.4 }}>
                        <DeleteIcon sx={{ fontSize: '0.95rem', color: 'text.disabled', '&:hover': { color: 'error.main' } }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

function DiaCard({ dia, blocos, expInicio, expFim, onAdd, onToggle, onDelete }) {
    const ativos = blocos.filter(b => b.ativo).length;
    const statusColor = blocos.length === 0 ? '#bbb' : ativos === 0 ? '#e57373' : ativos < blocos.length ? '#ffb74d' : '#1ABB9C';
    const totalMinutos = blocos
        .filter(b => b.ativo)
        .reduce((acc, b) => acc + toMinutes(b.horaFim) - toMinutes(b.horaInicio), 0);

    return (
        <Paper elevation={0} sx={{
            p: 2,
            borderRadius: 2,
            height: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderTop: `3px solid ${statusColor}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {dia.labelFull}
                </Typography>
                {blocos.length > 0 && (
                    <Chip
                        label={ativos > 0 ? `${Math.floor(totalMinutos / 60)}h${totalMinutos % 60 > 0 ? `${totalMinutos % 60}m` : ''}` : 'Pausado'}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            bgcolor: ativos > 0 ? 'rgba(26,187,156,0.12)' : 'action.hover',
                            color: ativos > 0 ? '#1ABB9C' : 'text.disabled',
                        }}
                    />
                )}
            </Box>

            <MiniTimeline blocos={blocos} expInicio={expInicio} expFim={expFim} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                {blocos.length === 0 ? (
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                        Nenhum horário configurado
                    </Typography>
                ) : (
                    blocos.map(b => (
                        <BlocoHorario key={b.id} bloco={b} onToggle={onToggle} onDelete={onDelete} />
                    ))
                )}
            </Box>

            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAdd(dia.key)}
                sx={{ mt: 1.5, fontSize: '0.72rem', color: '#1ABB9C', '&:hover': { bgcolor: 'rgba(26,187,156,0.08)' } }}
            >
                Adicionar
            </Button>
        </Paper>
    );
}

export default function Disponibilidade() {
    const [blocos, setBlocos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [parametros, setParametros] = useState(null);
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [gerarDialog, setGerarDialog] = useState(false);
    const [gerarRange, setGerarRange] = useState({
        inicio: toLocalDateString(new Date()),
        fim: toLocalDateString(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
    });
    const [gerando, setGerando] = useState(false);

    const expInicio = parametros?.horarioInicioExpediente ?? '07:00';
    const expFim    = parametros?.horarioFimExpediente    ?? '21:00';

    useEffect(() => {
        const fetchAll = async () => {
            await carregarParametros();
            await carregar();
        };
        fetchAll();
    }, []);

    const carregar = async () => {
        setLoading(true);
        try {
            const res = await api.get('/disponibilidade');
            const d = res.data;
            setBlocos(Array.isArray(d) ? d : (d?.content ?? []));
        } catch {
            mostrarMensagem('Erro ao carregar disponibilidade.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const carregarParametros = async () => {
        try {
            const res = await api.get('/parametros');
            if (res.data) {
                setParametros({
                    ...res.data,
                    horarioInicioExpediente: res.data.horarioInicioExpediente?.substring(0, 5) ?? '07:00',
                    horarioFimExpediente:    res.data.horarioFimExpediente?.substring(0, 5)    ?? '21:00',
                });
            }
        } catch { /* silencioso */ }
    };

    const blocosPorDia = (dia) => blocos.filter(b => b.diaSemana === dia);

    const totalAtivos = useMemo(() => blocos.filter(b => b.ativo).length, [blocos]);
    const totalDiasComAtivos = useMemo(() =>
        DIAS.filter(d => blocos.some(b => b.diaSemana === d.key && b.ativo)).length, [blocos]);

    const handleSalvar = async () => {
        if (!formData.diaSemana || !formData.horaInicio || !formData.horaFim) {
            mostrarMensagem('Preencha todos os campos.', 'warning');
            return;
        }
        if (formData.horaInicio >= formData.horaFim) {
            mostrarMensagem('Hora início deve ser anterior à hora fim.', 'warning');
            return;
        }
        if (parametros) {
            if (formData.horaInicio < expInicio || formData.horaFim > expFim) {
                setErrorMessage(`Horário fora do expediente configurado (${expInicio} – ${expFim}).`);
                setOpenErrorDialog(true);
                return;
            }
        }
        try {
            await api.post('/disponibilidade', formData, { skipGlobalError: true });
            mostrarMensagem(mensagemComAutoGen('Horário adicionado!'));
            setOpenDialog(false);
            setFormData(initialForm);
            await carregar();
        } catch (error) {
            // Sobreposição de horário chega como CONFLITO (409), e aí a mensagem
            // do servidor diz qual bloco colide.
            mostrarErro(error, 'salvar o horário');
        }
    };

    const mensagemComAutoGen = (base) => {
        if (parametros?.agendaGeracaoAutomatica) {
            const janela = parametros.agendaJanelaDias ?? 60;
            return `${base} Agenda sendo gerada automaticamente para os próximos ${janela} dias.`;
        }
        return `${base} Lembre-se de gerar a agenda manualmente no Calendário.`;
    };

    const handleToggle = async (id) => {
        try {
            const updated = await api.patch(`/disponibilidade/${id}/toggle`, null, { skipGlobalError: true });
            setBlocos(prev => prev.map(b => b.id === id ? updated.data : b));
            if (updated.data?.ativo) {
                mostrarMensagem(mensagemComAutoGen('Horário ativado!'));
            }
        } catch (error) {
            mostrarErro(error, 'alterar a situação do horário');
        }
    };

    const handleDeletar = async (id) => {
        const ok = await confirmAcao({
            title: 'Remover este horário?',
            text: 'Os horários livres futuros gerados a partir dele também saem da agenda. Sessões já marcadas não são afetadas.',
            confirmButtonText: 'Remover',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/disponibilidade/${id}`, { skipGlobalError: true });
            toastSuccess('Horário removido.');
            await carregar();
        } catch (error) {
            mostrarErro(error, 'remover o horário');
        }
    };

    const handleGerarAgenda = async () => {
        setGerando(true);
        try {
            const res = await api.post('/agenda/gerar', null, {
                params: { inicio: gerarRange.inicio, fim: gerarRange.fim }
            });
            mostrarMensagem(res.data?.mensagem || 'Agenda gerada.');
            setGerarDialog(false);
        } catch (e) {
            mostrarMensagem(e.response?.data?.message || 'Erro ao gerar agenda.', 'error');
        } finally {
            setGerando(false);
        }
    };

    const abrirDialog = (diaKey = '') => {
        setFormData({ ...initialForm, diaSemana: diaKey });
        setOpenDialog(true);
    };

    const mostrarMensagem = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Disponibilidade</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {totalDiasComAtivos > 0
                            ? `${totalDiasComAtivos} dia${totalDiasComAtivos > 1 ? 's' : ''} ativo${totalDiasComAtivos > 1 ? 's' : ''} · ${totalAtivos} bloco${totalAtivos > 1 ? 's' : ''} configurado${totalAtivos > 1 ? 's' : ''} · Expediente ${expInicio}–${expFim}`
                            : `Nenhum horário ativo · Expediente ${expInicio}–${expFim}`
                        }
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<EventNoteIcon />}
                        onClick={() => setGerarDialog(true)}>
                        Gerar Agenda
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirDialog()}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#159c82' } }}>
                        Novo Horário
                    </Button>
                </Box>
            </Box>

            {parametros && (
                <Alert
                    severity={parametros.agendaGeracaoAutomatica ? 'success' : 'warning'}
                    sx={{ mb: 2 }}
                >
                    {parametros.agendaGeracaoAutomatica
                        ? `Geração automática de agenda ATIVA. Ao salvar ou ativar um horário, a agenda dos próximos ${parametros.agendaJanelaDias ?? 60} dias é gerada automaticamente.`
                        : 'Geração automática DESATIVADA. Após alterar disponibilidades, vá ao Calendário e clique em "Gerar Agenda".'}
                </Alert>
            )}

            <Grid container spacing={2}>
                {DIAS.map(dia => (
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={dia.key}>
                        <DiaCard
                            dia={dia}
                            blocos={blocosPorDia(dia.key)}
                            expInicio={expInicio}
                            expFim={expFim}
                            onAdd={abrirDialog}
                            onToggle={handleToggle}
                            onDelete={handleDeletar}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Dialog: Novo Horário */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Novo Horário Disponível</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField select fullWidth required label="Dia da Semana"
                            value={formData.diaSemana}
                            onChange={e => setFormData({ ...formData, diaSemana: e.target.value })}>
                            {DIAS.map(d => <MenuItem key={d.key} value={d.key}>{d.labelFull}</MenuItem>)}
                        </TextField>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField fullWidth required label="Hora Início" type="time"
                                value={formData.horaInicio}
                                onChange={e => setFormData({ ...formData, horaInicio: e.target.value })}
                                InputLabelProps={{ shrink: true }} inputProps={{ step: '300' }} />
                            <TextField fullWidth required label="Hora Fim" type="time"
                                value={formData.horaFim}
                                onChange={e => setFormData({ ...formData, horaFim: e.target.value })}
                                InputLabelProps={{ shrink: true }} inputProps={{ step: '300' }} />
                        </Box>
                        {parametros && (
                            <Typography variant="caption" color="text.secondary">
                                Expediente permitido: {expInicio} – {expFim}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch checked={formData.ativo}
                                onChange={e => setFormData({ ...formData, ativo: e.target.checked })} />
                            <Typography variant="body2">{formData.ativo ? 'Ativo' : 'Inativo'}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleSalvar} variant="contained"
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#159c82' } }}>
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Gerar Agenda */}
            <Dialog open={gerarDialog} onClose={() => setGerarDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Gerar Agenda</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Gera os slots disponíveis com base nas disponibilidades ativas.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField fullWidth label="Data Início" type="date"
                            value={gerarRange.inicio}
                            onChange={e => setGerarRange(p => ({ ...p, inicio: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                        <TextField fullWidth label="Data Fim" type="date"
                            value={gerarRange.fim}
                            onChange={e => setGerarRange(p => ({ ...p, fim: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setGerarDialog(false)}>Cancelar</Button>
                    <Button onClick={handleGerarAgenda} variant="contained" disabled={gerando}
                        startIcon={gerando ? <CircularProgress size={18} color="inherit" /> : null}>
                        Gerar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Erro de horário */}
            <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>Horário inválido</DialogTitle>
                <DialogContent>
                    <DialogContentText>{errorMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenErrorDialog(false)} autoFocus>OK</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
